import {
  Analytics,
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
  setUserProperties,
} from 'firebase/analytics';

import { app } from './auth';
import store from '../../redux/store';
import { APP_NAME, APP_VERSION } from '../config/appMeta';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

let analytics: Analytics | null = null;
let currentUserId: string | null = null;

const appContext = {
  app_name: APP_NAME,
  app_version: APP_VERSION,
};

const pushAppContextToDataLayer = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(appContext);
};

const setFirebaseAppContext = () => {
  if (!analytics) {
    return;
  }

  setUserProperties(analytics, appContext);
};

export const initAnalytics = async () => {
  if (analytics || typeof window === 'undefined') {
    return;
  }

  if (typeof window.dataLayer === 'undefined') {
    window.dataLayer = [];
  }

  pushAppContextToDataLayer();

  if (await isSupported()) {
    analytics = getAnalytics(app);
    setFirebaseAppContext();
  }

  const persistedUserId = store.getState()?.auth?.user?.id;

  if (persistedUserId) {
    setAnalyticsUserId(String(persistedUserId));
  }
};

export const setAnalyticsUserId = (userId: string) => {
  currentUserId = userId;

  if (analytics) {
    setUserId(analytics, userId);
  }

  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ user_id: userId });
  }
};

export const clearAnalyticsUserId = () => {
  currentUserId = null;

  if (analytics) {
    setUserId(analytics, null);
  }

  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ user_id: undefined });
  }
};

export type ItemVariant = 'single' | 'recurrence' | 'plan' | 'service';

export type AnalyticsCoupon = {
  id?: string | number;
  attributes?: {
    name?: string;
  };
};

type EcommerceItem = {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_brand?: string;
  item_variant?: ItemVariant;
  coupon?: string;
};

type EcommerceParams = {
  currency?: string;
  value: number;
  items: EcommerceItem[];
  purchase_type?: string;
  payment_type?: string;
  transaction_id?: string;
  coupon?: string;
};

export const getCouponCode = (coupon?: AnalyticsCoupon | null): string | undefined => {
  if (!coupon?.id) {
    return undefined;
  }

  return coupon.attributes?.name || String(coupon.id);
};

export const buildEcommerceItem = ({
  id,
  name,
  price,
  quantity = 1,
  itemBrand,
  itemVariant,
  coupon,
}: {
  id: string | number;
  name: string;
  price: number;
  quantity?: number;
  itemBrand?: string;
  itemVariant?: ItemVariant;
  coupon?: AnalyticsCoupon | null;
}): EcommerceItem => {
  const couponCode = getCouponCode(coupon);

  return {
    item_id: String(id),
    item_name: name,
    price,
    quantity,
    ...(itemBrand && { item_brand: itemBrand }),
    ...(itemVariant && { item_variant: itemVariant }),
    ...(couponCode && { coupon: couponCode }),
  };
};

export const buildEcommerceParams = ({
  id,
  name,
  price,
  quantity = 1,
  itemBrand,
  itemVariant,
  coupon,
  purchaseType,
}: {
  id: string | number;
  name: string;
  price: number;
  quantity?: number;
  itemBrand?: string;
  itemVariant?: ItemVariant;
  coupon?: AnalyticsCoupon | null;
  purchaseType?: string;
}): EcommerceParams => {
  const couponCode = getCouponCode(coupon);

  return {
    currency: 'BRL',
    value: price,
    purchase_type: purchaseType,
    ...(couponCode && { coupon: couponCode }),
    items: [buildEcommerceItem({
      id,
      name,
      price,
      quantity,
      itemBrand,
      itemVariant,
      coupon,
    })],
  };
};

const buildEventParams = (params: EcommerceParams) => ({
  ...appContext,
  currency: params.currency ?? 'BRL',
  value: params.value,
  items: params.items,
  purchase_type: params.purchase_type,
  payment_type: params.payment_type,
  transaction_id: params.transaction_id,
  ...(params.coupon && { coupon: params.coupon }),
  ...(currentUserId && { user_id: currentUserId }),
});

const pushToDataLayer = (eventName: string, params: EcommerceParams) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: eventName,
    ...appContext,
    ...(currentUserId && { user_id: currentUserId }),
    purchase_type: params.purchase_type,
    payment_type: params.payment_type,
    ...(params.coupon && { coupon: params.coupon }),
    ecommerce: {
      currency: params.currency ?? 'BRL',
      value: params.value,
      items: params.items,
      transaction_id: params.transaction_id,
      payment_type: params.payment_type,
      ...(params.coupon && { coupon: params.coupon }),
    },
  });
};

const trackEvent = (eventName: string, params: EcommerceParams) => {
  const eventParams = buildEventParams(params);

  if (analytics) {
    logEvent(analytics, eventName, eventParams);
  }

  pushToDataLayer(eventName, params);
};

export const trackViewItem = (params: EcommerceParams) => {
  trackEvent('view_item', params);
};

export const trackAddToCart = (params: EcommerceParams) => {
  trackEvent('add_to_cart', params);
};

export const trackBeginCheckout = (params: EcommerceParams) => {
  trackEvent('begin_checkout', params);
};

export const trackAddPaymentInfo = (params: EcommerceParams) => {
  trackEvent('add_payment_info', params);
};

export const trackPurchase = (params: EcommerceParams & { transaction_id: string }) => {
  trackEvent('purchase', params);
};
